import {
    ClickIconRenderer,
    DataSourceLookup,
    DateComp,
    DisplayBool,
    DisplayBoolRadio,
    DisplayCountry,
    DisplayDeviceStatus,
    DisplayFolder,
    DisplayGroupContestantStatistics,
    DisplayMisses,
    DisplaySeparator,
    DisplayTags,
    DisplayText,
    DisplayTextWithRoute,
    DisplayTick,
    DisplayTimespan,
    DuplicateArrayRenderer,
    GridButton,
    InputBool,
    InputColor,
    InputDateTime,
    InputNullableDateTime,
    InputTimespan,
    InputNullableTimespan,
    InputInt,
    InputKilometer,
    InputNullableFloat,
    InputNullableInt,
    InputPosition,
    InputRadio,
    InputSelect,
    InputText,
    LinkToNewWindow,
    ValidatedFloat,
    ValidatedText,
    WeatherIcon,
    DisplayElapsedTime,
    ValidatedInt,
} from '@/components';
import InputFloat from '@/components/Basic/InputFloat.vue';
import { ImportStatusRenderer, ImportValueEditor } from '@/components/Import';
import ImportCellRenderer from '@/components/Import/ImportCellRenderer.vue';
import BibAssignSortSourceEditor from '@/components/views/BibAssign/BibAssignSortSourceEditor.vue';
import BibAssignSortSourceRenderer from '@/components/views/BibAssign/BibAssignSortSourceRenderer.vue';
import ContestantShootingDisplayPoint from '@/components/views/Contestants/ContestantShootingDisplayPoint.vue';
import ContestantTimeWithEditButton from '@/components/views/Contestants/ContestantTimeWithEditButton.vue';
import AntennaStatus from '@/components/views/DeviceStatus/AntennaStatus.vue';
import BatteryStatus from '@/components/views/DeviceStatus/BatteryStatus.vue';
import DeviceDetailIcon from '@/components/views/DeviceStatus/DeviceDetailIcon.vue';
import DeviceStatusDisplayEvent from '@/components/views/DeviceStatus/DeviceStatusDisplayEvent.vue';
import DeviceStatusMessages from '@/components/views/DeviceStatus/DeviceStatusMessages.vue';
import IsConnectedIcon from '@/components/views/DeviceStatus/IsConnectedIcon.vue';
import EngineHandlerActions from '@/components/views/EngineHandler/EngineHandlerActions.vue';
import MatrixDnxRenderer from '@/components/views/Matrix/SubComponents/MatrixDnxRenderer.vue';
import MatrixStationRoleCellEditor from '@/components/views/Matrix/SubComponents/MatrixStationRoleCellEditor.vue';
import MatrixStationRoleCellRenderer from '@/components/views/Matrix/SubComponents/MatrixStationRoleCellRenderer.vue';
import DisplayStationLastActivity from '@/components/views/Station/DisplayStationLastActivity.vue';
import TrackingDeviceAccuracy from '@/components/views/Tracking/TrackingDeviceAccuracy.vue';
import TrackingListBattery from '@/components/views/Tracking/TrackingListBattery.vue';
import TrackingListGridButton from '@/components/views/Tracking/TrackingListGridButton.vue';
import type { ClipboardDataItem } from '@/composeables/clipboard/clipboardData';
import { dateTimeToMilliseconds, formatDateDefault, parseTimespanIntoStringFromFormat, tryParseTimestring } from '@/utilities/dateutils';
import { StationAlias } from '../generated';
import StationAliasTimeshiftCellRenderer from '@/components/views/Station/StationAliasTimeshiftCellRenderer.vue';
import DisplayRaceLabels from '@/components/views/Races/DisplayRaceLabels.vue';
import PrintResult from '@/components/views/RaceControl/PrintResult.vue';
import Participant2FARenderer from '@/components/views/Participant/Participant2FARenderer.vue';
import ParticipantSignupStatusButton from '@/components/views/Participant/ParticipantSignupStatusButton.vue';
import ParticipantCategoryDisplay from '@/components/views/Participant/ParticipantCategoryDisplay.vue';
import DisplayGroupStartTimeSelector from '@/components/Groups/DisplayGroupStartTimeSelector.vue';
import GPIOReadingUsedByCellRenderer from '@/components/views/GPIOReadings/GPIOReadingUsedByCellRenderer.vue';
import ContestantShootingToggleStateButton from '@/components/views/Contestants/ContestantShootingToggleStateButton.vue';
import RaceContestantExitStatusSelect from '@/components/RaceContestant/RaceContestantExitStatusSelect.vue';
import RaceContestantExitStatusLookup from '@/components/RaceContestant/RaceContestantExitStatusLookup.vue';
import BibAssignFromToTime from '@/components/views/BibAssign/BibAssignFromToTime.vue';
import FocusEventFromToHelper from '@/components/focuseventcard/FocusEventFromToHelper.vue';
import HandleDestinationEMITID from '@/components/ExternalData/HandleDestinationEMITID.vue';
import HandleSourceEMITID from '@/components/ExternalData/HandleSourceEMITID.vue';
import HandleDestinationRace from '@/components/ExternalData/HandleDestinationRace.vue';
import HandleSourceRace from '@/components/ExternalData/HandleSourceRace.vue';
import HandleDestinationCategory from '@/components/ExternalData/HandleDestinationCategory.vue';
import HandleSourceCategory from '@/components/ExternalData/HandleSourceCategory.vue';
import HandleDestinationClub from '@/components/ExternalData/HandleDestinationClub.vue';
import HandleSourceClub from '@/components/ExternalData/HandleSourceClub.vue';
import DisplayParticipantID from '@/components/ExternalData/DisplayParticipantID.vue';
import { TimeSpan } from '../TimeSpan';
import ShowOrAddContestant from '@/components/views/Bibs/ShowOrAddContestant.vue';
import StationRoleLastActivity from '@/components/views/StationRoles/StationRoleLastActivity.vue';
import StartedProgress from '@/components/views/RaceControl/StartedProgress.vue';
import FinishedProgress from '@/components/views/RaceControl/FinishedProgress.vue';
import DropdownSelectGuiDefWrapper from '@/components/Basic/DropdownSelectGuiDefWrapper.vue';
import RelayContestantFirstnameWithSearch from '@/components/views/Contestants/RelayContestantFirstnameWithSearch.vue';
import ContestantRelayLegnumberRenderer from '@/components/views/Contestants/ContestantRelayLegnumberRenderer.vue';
import GPIOReadingUsedForCellRenderer from '@/components/views/GPIOReadings/GPIOReadingUsedForCellRenderer.vue';
import LivePublished from '@/components/FileArchive/LivePublished.vue';

const supportedEditors = () => [
    DateComp,
    InputDateTime,
    InputNullableDateTime,
    ValidatedFloat,
    ValidatedText,
    ValidatedInt,
    InputFloat,
    InputInt,
    InputNullableInt,
    InputNullableFloat,
    InputText,
    InputBool,

    // TODO: these should be implemented
    DisplayTimespan,
    InputTimespan,
    InputNullableTimespan,
    DisplayBoolRadio,
    DisplayBool,
    DisplayCountry,
    DropdownSelectGuiDefWrapper,

    DisplayElapsedTime,

    // Everything belov here should probably NOT be able to copy, but if we need to copy it we need to implement it properly
    // These will all raise an exception so pasting is cancelled
    ...unsupportedEditors(),
];

/**
 * Contains a list of all the editors that we should throw an exception when trying to paste data into them
 * The exception will cancel the paste operation
 * @returns
 */
const unsupportedEditors = () => [
    InputSelect,
    DataSourceLookup,
    DisplayTextWithRoute,
    DisplayTick,
    ContestantTimeWithEditButton,
    ContestantShootingDisplayPoint,
    ClickIconRenderer,
    LinkToNewWindow,
    ClickIconRenderer,
    DuplicateArrayRenderer,
    GridButton,
    DisplayText,
    MatrixStationRoleCellEditor,
    MatrixDnxRenderer,
    EngineHandlerActions,
    DeviceStatusMessages,
    AntennaStatus,
    BatteryStatus,
    IsConnectedIcon,
    DeviceStatusDisplayEvent,
    DeviceDetailIcon,
    BibAssignSortSourceRenderer,
    MatrixStationRoleCellRenderer,
    BibAssignSortSourceEditor,
    ImportCellRenderer,
    ImportValueEditor,
    ImportStatusRenderer,
    TrackingDeviceAccuracy,
    TrackingListBattery,
    TrackingListGridButton,
    DisplayStationLastActivity,
    StationAliasTimeshiftCellRenderer,
    DisplayRaceLabels,
    PrintResult,
    Participant2FARenderer,
    ParticipantSignupStatusButton,
    ParticipantCategoryDisplay,
    DisplayCountry,
    DisplayGroupStartTimeSelector,
    DisplayGroupContestantStatistics,
    GPIOReadingUsedByCellRenderer,
    ContestantShootingToggleStateButton,
    DisplayMisses,
    RaceContestantExitStatusSelect,
    RaceContestantExitStatusLookup,
    BibAssignFromToTime,
    FocusEventFromToHelper,
    DisplayFolder,
    DisplayTags,
    HandleDestinationEMITID,
    HandleSourceEMITID,
    HandleDestinationRace,
    HandleSourceRace,
    HandleDestinationCategory,
    HandleSourceCategory,
    HandleDestinationClub,
    HandleSourceClub,
    DisplayParticipantID,
    InputPosition,
    DisplayDeviceStatus,
    DisplaySeparator,
    InputKilometer,
    InputColor,
    ShowOrAddContestant,
    StationRoleLastActivity,
    StartedProgress,
    FinishedProgress,

    ContestantRelayLegnumberRenderer,
    RelayContestantFirstnameWithSearch,
    GPIOReadingUsedForCellRenderer,
    WeatherIcon,
    LivePublished,
];

export type SuportedEditorTypes = ReturnType<typeof supportedEditors>[number];

/**
 * Helper function to get the value for InputDateTime editor from the clipboard item based on whattype fo data we re trying tonpaste into it.
 * @param item
 * @returns
 */
const convertClipboardDataToDateTime = (item: ClipboardDataItem): Date | null => {
    // We need to normalize the date and see if it's valid
    if (item.value instanceof Date) {
        return item.value;
    } else {
        // We try and parse the value as a date
        const parsedDate = new Date(item.value);
        if (!isNaN(parsedDate.getTime())) {
            return parsedDate;
        }
    }
    return null;
};

/**
 * Convert clipboard data to timespan value
 * @param item
 * @returns
 */
const convertClipboardDataToTimespan = (item: ClipboardDataItem): TimeSpan | null => {
    // We try and parse the timespan from the string
    // The value can be either a number (milliseconds) or a string (formatted timespan)
    if (item.value instanceof TimeSpan) {
        return item.value;
    } else if (typeof item.value === 'number') {
        return TimeSpan.FromMilliseconds(item.value);
    } else if (typeof item.value === 'string') {
        // Check if we can parse it as a date first
        const parsedDate = new Date(item.value);
        if (!isNaN(parsedDate.getTime())) {
            // We have a valid date, we can get the timespan from the date
            const ms = dateTimeToMilliseconds(parsedDate);

            return TimeSpan.FromMilliseconds(ms);
        } else {
            // We try and parse as common timespan format
            let parsedTime = tryParseTimestring(item.text, 'h:m:s.f');
            parsedTime = parsedTime ?? tryParseTimestring(item.text, 'm:s.f');
            parsedTime = parsedTime ?? tryParseTimestring(item.text, 'h:m:s');

            if (parsedTime) {
                const ms = dateTimeToMilliseconds(parsedTime);

                return TimeSpan.FromMilliseconds(ms);
            }
        }
        console.log('We try and parse the string value either a date or just a time. via javascript Date', item.value);
    }

    return null;
};

/**
 * This should be the value that is shown in the clipboard when copying from the grid
 * @remarks This should be the same value as it is shown in the editor cell renderer
 * @param editor
 * @param value
 * @param params
 * @returns
 */
export const ConvertCellRendererValueToString = (editor: SuportedEditorTypes, value: any, params?: any): string => {
    if (editor == DateComp) {
        return formatDateDefault(value, params?.format).replace(/,/g, '');
    } else if (editor == ValidatedText) {
        return `${value}`;
    } else if (editor == InputBool) {
        return value ? 'true' : 'false';
    } else if (editor == DisplayTimespan) {
        return parseTimespanIntoStringFromFormat(value, params?.format).replace(/,/g, '');
    }
    return `${value}`;
};

/**
 * This should be the value that is provided for the editor when opening the cell editor
 * @remarks The value should then always be the actual value that the vModel of the cellEditor expects
 * @param sourceEditor
 * @param item
 * @param params
 * @returns
 * @throws Error if the editor type is not supported. This will cancel the paste operation
 */
export const ConvertClipboardDataToCellEditorValue = (sourceEditor: SuportedEditorTypes, item: ClipboardDataItem, params?: any) => {
    // If the editor is not supported, we throw an error
    if (!supportedEditors().includes(sourceEditor)) {
        throw new Error(`Unsupported editor type for clipboard paste: ${sourceEditor} `);
    }

    if (sourceEditor == InputDateTime || sourceEditor == InputNullableDateTime) {
        return convertClipboardDataToDateTime(item);
    } else if (sourceEditor == InputTimespan) {
        // We need to try and parse the timespan
        return convertClipboardDataToTimespan(item);
    } else if (sourceEditor == InputText) {
        return item.text;
    } else if (sourceEditor == InputInt || sourceEditor == InputFloat) {
        // We try and return a number
        if (!isNaN(Number(item.value))) {
            return Number(item.value);
        }

        throw new Error(`Cannot convert clipboard value to number for editor ${sourceEditor}`);
    } else if (sourceEditor == InputBool) {
        if (item.value === true) {
            return true;
        }
        return false;
    } else if (sourceEditor == InputNullableInt || sourceEditor == InputNullableFloat) {
        if (item.value === null || item.value === undefined || item.value === '') {
            return null;
        }
        if (!isNaN(Number(item.value))) {
            return Number(item.value);
        }
    }
    throw new Error(`Unsupported editor type for clipboard paste: ${sourceEditor} `);
};
